import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { IoMdArrowBack } from "react-icons/io";
import { ChevronRight, ChevronDown, Check, ShieldCheck } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import PinKeypad from "../../components/settings/parental-controls/PinKeypad";
import OtpInput from "../../components/settings/parental-controls/OtpInput";
import {
    fetchParentalStatus,
    fetchLinkedParentEmail,
    sendParentEmail,
    verifyParentEmail,
    createParentalPin,
    unlockParentalPin,
    updateParentalSettings,
    requestPinReset,
    verifyPinReset,
    confirmPinReset,
    fetchUsageToday,
    fetchAuditLog,
    clearUnlockToken,
    getErrorMessage,
} from "../../redux/ApiSlice/ParentalControlSlice";

const PIN_LENGTH = 4;

const TIME_LIMIT_OPTIONS = [
    { label: "No Limit", value: null },
    { label: "30 minutes", value: 30 },
    { label: "40 minutes", value: 40 },
    { label: "1 hour", value: 60 },
    { label: "1.5 hours", value: 90 },
    { label: "2 hours", value: 120 },
    { label: "3 hours", value: 180 },
];

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const maskEmail = (email) => {
    if (!email) return "";
    const [local, domain] = email.split("@");
    if (!domain) return email;
    const visible = local.slice(0, 1);
    return `${visible}${"*".repeat(Math.max(local.length - 1, 3))}@${domain}`;
};

const formatUsage = (seconds) => {
    const totalMinutes = Math.floor((seconds || 0) / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours <= 0) return `${minutes} min`;
    return `${hours} hr ${minutes} min`;
};

const formatAuditDate = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
};

const SettingsHeader = ({ title, onBack }) => (
    <div className="sticky top-0 z-50">
        <div className="relative flex items-center justify-center h-12 bg-[#BDE6FF] rounded-b-xl px-2">
            <IoMdArrowBack
                onClick={onBack}
                className="absolute left-2 top-1/2 transition-transform -translate-y-1/2 text-[22px] text-black cursor-pointer"
            />
            <p className="text-center font-bold text-[16px] text-black flex-grow">{title}</p>
        </div>
    </div>
);

const ParentalControls = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const unlockToken = useSelector((state) => state.ParentalControl.unlockToken);
    const unlockTokenExpiresAt = useSelector((state) => state.ParentalControl.unlockTokenExpiresAt);
    const linkedEmail = useSelector((state) => state.ParentalControl.linkedEmail);
    const usageToday = useSelector((state) => state.ParentalControl.usageToday);
    const auditLog = useSelector((state) => state.ParentalControl.auditLog);
    const verifyEmailLoading = useSelector((state) => state.ParentalControl.verifyEmailLoading);
    const pinResetVerifyLoading = useSelector((state) => state.ParentalControl.pinResetVerifyLoading);
    const updateSettingsLoading = useSelector((state) => state.ParentalControl.updateSettingsLoading);

    const [step, setStep] = useState("loading");
    const [otpMode, setOtpMode] = useState("setup"); // "setup" | "reset"
    const [resetOrigin, setResetOrigin] = useState("dashboard"); // "dashboard" | "enterPin"
    const [resumedAfterVerify, setResumedAfterVerify] = useState(false);

    const [parentEmailInput, setParentEmailInput] = useState("");
    const [pendingEmail, setPendingEmail] = useState("");
    const [savedEmail, setSavedEmail] = useState("");

    const [newPin, setNewPin] = useState("");
    const [confirmPin, setConfirmPin] = useState("");
    const [enterPin, setEnterPin] = useState("");
    const [enterPinError, setEnterPinError] = useState("");

    const [neighborhoodAllowed, setNeighborhoodAllowed] = useState(true);
    const [dailyLimitMinutes, setDailyLimitMinutes] = useState(90);
    const [saving, setSaving] = useState(false);
    const [neighborhoodSaving, setNeighborhoodSaving] = useState(false);
    const [timeLimitOpen, setTimeLimitOpen] = useState(false);
    const [recentChangesOpen, setRecentChangesOpen] = useState(false);

    const loadDashboard = async (token) => {
        try {
            const statusRes = await dispatch(fetchParentalStatus(token ? { unlock_token: token } : {})).unwrap();
            setSavedEmail(statusRes.parent_email || "");
            setNeighborhoodAllowed(statusRes.neighborhood_allowed ?? true);
            setDailyLimitMinutes(statusRes.daily_time_limit_minutes ?? null);
        } catch (err) {
            toast.error(getErrorMessage(err));
            if (err?.status === 403 || err?.status === 423) {
                dispatch(clearUnlockToken());
                setEnterPinError(err.status === 423 ? getErrorMessage(err) : "");
                setStep("enterPin");
            }
            return;
        }
        dispatch(fetchUsageToday(token ? { unlock_token: token } : {}));
        dispatch(fetchAuditLog(token ? { unlock_token: token } : {}));
        setStep("dashboard");
    };

    useEffect(() => {
        (async () => {
            if (unlockToken && unlockTokenExpiresAt && unlockTokenExpiresAt > Date.now()) {
                await loadDashboard(unlockToken);
                return;
            }
            try {
                const statusRes = await dispatch(fetchParentalStatus({})).unwrap();
                if (statusRes.is_pin_set) {
                    setSavedEmail(statusRes.parent_email || "");
                    setStep("enterPin");
                } else if (statusRes.is_parent_verified) {
                    setPendingEmail(statusRes.parent_email || "");
                    setResumedAfterVerify(true);
                    setStep("createPin");
                } else {
                    setStep("intro");
                }
            } catch (err) {
                const message = getErrorMessage(err);
                if (err?.status === 403 && /not a teen account/i.test(message)) {
                    toast.error(message);
                    navigate("/account/settings");
                } else if (err?.status === 403) {
                    // A PIN already exists - the server needs a credential to return status.
                    dispatch(fetchLinkedParentEmail({}));
                    setStep("enterPin");
                } else if (err?.status === 423) {
                    dispatch(fetchLinkedParentEmail({}));
                    setStep("enterPin");
                    setEnterPinError(message);
                } else {
                    toast.error(message);
                    setStep("intro");
                }
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sendOtp = async (email, mode) => {
        try {
            const res = await dispatch(sendParentEmail({ parent_email: email })).unwrap();
            setPendingEmail(email);
            setOtpMode(mode);
            toast.success(res?.message ? `${res.message} (${maskEmail(email)})` : `Verification code sent to ${maskEmail(email)}`);
            setStep("otp");
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
    };

    const handleSendCode = () => {
        const trimmed = parentEmailInput.trim();
        if (!isValidEmail(trimmed)) {
            toast.error("Enter a valid parent or guardian email");
            return;
        }
        sendOtp(trimmed, "setup");
    };

    const handleResendOtp = async () => {
        try {
            if (otpMode === "setup") {
                const res = await dispatch(sendParentEmail({ parent_email: pendingEmail })).unwrap();
                toast.success(res?.message || `New code sent to ${maskEmail(pendingEmail)}`);
            } else {
                const res = await dispatch(requestPinReset()).unwrap();
                toast.success(res?.message || `New code sent to ${savedEmail}`);
            }
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
    };

    const handleVerifyOtp = async (code) => {
        try {
            if (otpMode === "setup") {
                await dispatch(verifyParentEmail({ otp: code })).unwrap();
                setNewPin("");
                setConfirmPin("");
                setStep("createPin");
            } else {
                await dispatch(verifyPinReset({ otp: code })).unwrap();
                setNewPin("");
                setConfirmPin("");
                setStep("resetPin");
            }
            return true;
        } catch (err) {
            toast.error(getErrorMessage(err));
            return false;
        }
    };

    useEffect(() => {
        if (step === "createPin" && newPin.length === PIN_LENGTH) {
            setConfirmPin("");
            setStep("confirmPin");
        }
    }, [newPin, step]);

    useEffect(() => {
        if (step !== "confirmPin" || confirmPin.length !== PIN_LENGTH) return;

        if (confirmPin !== newPin) {
            toast.error("PINs do not match. Please try again.");
            setNewPin("");
            setConfirmPin("");
            setStep("createPin");
            return;
        }

        (async () => {
            try {
                const res = await dispatch(createParentalPin({ pin: newPin, confirm_pin: confirmPin })).unwrap();
                toast.success(res?.message || "Parental controls are now active");
                const unlockRes = await dispatch(unlockParentalPin({ parent_pin: newPin })).unwrap();
                await loadDashboard(unlockRes.unlock_token);
            } catch (err) {
                toast.error(getErrorMessage(err));
                setNewPin("");
                setConfirmPin("");
                setStep("createPin");
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [confirmPin, newPin, step]);

    useEffect(() => {
        if (step === "resetPin" && newPin.length === PIN_LENGTH) {
            setConfirmPin("");
            setStep("resetConfirmPin");
        }
    }, [newPin, step]);

    useEffect(() => {
        if (step !== "resetConfirmPin" || confirmPin.length !== PIN_LENGTH) return;

        if (confirmPin !== newPin) {
            toast.error("PINs do not match. Please try again.");
            setNewPin("");
            setConfirmPin("");
            setStep("resetPin");
            return;
        }

        (async () => {
            try {
                const res = await dispatch(confirmPinReset({ pin: newPin, confirm_pin: confirmPin })).unwrap();
                toast.success(res?.message || "PIN updated successfully");
                const unlockRes = await dispatch(unlockParentalPin({ parent_pin: newPin })).unwrap();
                await loadDashboard(unlockRes.unlock_token);
            } catch (err) {
                toast.error(getErrorMessage(err));
                setNewPin("");
                setConfirmPin("");
                setStep("resetPin");
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [confirmPin, newPin, step]);

    useEffect(() => {
        if (step !== "enterPin" || enterPin.length !== PIN_LENGTH) return;

        (async () => {
            try {
                const unlockRes = await dispatch(unlockParentalPin({ parent_pin: enterPin })).unwrap();
                setEnterPinError("");
                setEnterPin("");
                await loadDashboard(unlockRes.unlock_token);
            } catch (err) {
                setEnterPinError(getErrorMessage(err, "Incorrect PIN. Please try again."));
                setEnterPin("");
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enterPin, step]);

    const handleForgotPin = async (origin) => {
        try {
            const res = await dispatch(requestPinReset()).unwrap();
            setResetOrigin(origin);
            setOtpMode("reset");
            toast.success(res?.message || `Verification code sent to ${savedEmail}`);
            setStep("otp");
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
    };

    const handleToggleNeighborhood = async () => {
        const nextValue = !neighborhoodAllowed;
        setNeighborhoodAllowed(nextValue);
        setNeighborhoodSaving(true);
        try {
            const res = await dispatch(
                updateParentalSettings({
                    neighborhood_allowed: nextValue,
                    ...(unlockToken ? { unlock_token: unlockToken } : {}),
                })
            ).unwrap();
            setNeighborhoodAllowed(res.neighborhood_allowed ?? nextValue);
            toast.success("Neighborhood access updated");
            dispatch(fetchAuditLog(unlockToken ? { unlock_token: unlockToken } : {}));
        } catch (err) {
            setNeighborhoodAllowed(!nextValue);
            toast.error(getErrorMessage(err));
            if (err?.status === 403 || err?.status === 423) {
                dispatch(clearUnlockToken());
                setStep("enterPin");
            }
        } finally {
            setNeighborhoodSaving(false);
        }
    };

    const handleSaveDashboard = async () => {
        setSaving(true);
        try {
            const res = await dispatch(
                updateParentalSettings({
                    neighborhood_allowed: neighborhoodAllowed,
                    daily_time_limit_minutes: dailyLimitMinutes,
                    ...(unlockToken ? { unlock_token: unlockToken } : {}),
                })
            ).unwrap();
            setNeighborhoodAllowed(res.neighborhood_allowed ?? neighborhoodAllowed);
            setDailyLimitMinutes(res.daily_time_limit_minutes ?? dailyLimitMinutes);
            toast.success("Parental controls updated");
            dispatch(fetchAuditLog(unlockToken ? { unlock_token: unlockToken } : {}));
        } catch (err) {
            toast.error(getErrorMessage(err));
            if (err?.status === 403 || err?.status === 423) {
                dispatch(clearUnlockToken());
                setStep("enterPin");
            }
        } finally {
            setSaving(false);
        }
    };

    const goBack = () => {
        switch (step) {
            case "intro":
            case "enterPin":
            case "dashboard":
                navigate("/account/settings");
                break;
            case "email":
                setStep("intro");
                break;
            case "otp":
                setStep(otpMode === "setup" ? "email" : resetOrigin);
                break;
            case "createPin":
                setStep(resumedAfterVerify ? "intro" : "otp");
                break;
            case "confirmPin":
                setNewPin("");
                setStep("createPin");
                break;
            case "resetPin":
                setStep("otp");
                break;
            case "resetConfirmPin":
                setNewPin("");
                setStep("resetPin");
                break;
            default:
                navigate("/account/settings");
        }
    };

    const headerTitles = {
        loading: "Parental Controls",
        intro: "Parental Controls",
        email: "Parental Controls",
        otp: "Verify Parent Email",
        createPin: "Create PIN",
        confirmPin: "Confirm PIN",
        resetPin: "Create New PIN",
        resetConfirmPin: "Confirm New PIN",
        enterPin: "Parental Controls",
        dashboard: "Parental Controls",
    };

    if (step === "loading") {
        return (
            <div className="h-full bg-white dark:bg-[#1E1D21] flex items-center justify-center">
                <p className="text-black dark:text-white text-[14px]">Loading...</p>
            </div>
        );
    }

    return (
        <div className="h-full bg-white dark:bg-[#1E1D21] overflow-y-auto">
            <SettingsHeader title={headerTitles[step]} onBack={goBack} />

            {step === "intro" && (
                <div className="flex flex-col items-center px-4 pt-8 text-center">
                    <ShieldCheck size={48} className="text-[#4cb6f9] mb-4" />
                    <p className="text-[15px] font-bold text-black dark:text-white mb-2">
                        Set up Parental Controls
                    </p>
                    <p className="text-[12px] text-gray-600 dark:text-gray-400 mb-8 max-w-sm">
                        A parent or guardian can manage Neighborhood access and this account's
                        daily time limit. Verify a parent email and create a PIN to get started.
                    </p>
                    <button
                        onClick={() => setStep("email")}
                        className="w-full max-w-md py-3 rounded-lg text-[15px] font-bold bg-[#BDE6FF] text-black"
                    >
                        Continue
                    </button>
                </div>
            )}

            {step === "email" && (
                <div className="flex flex-col items-center px-4 pt-8">
                    <p className="text-[14px] text-center text-black dark:text-white mb-6 max-w-sm">
                        Enter the email address of a parent or guardian. We'll send a verification code.
                    </p>
                    <input
                        type="email"
                        value={parentEmailInput}
                        onChange={(e) => setParentEmailInput(e.target.value)}
                        placeholder="parent@example.com"
                        className="w-full max-w-md px-3 py-3 mb-6 text-[14px] bg-white dark:bg-[#333333] text-black dark:text-white border-2 border-[#BDE6FF] dark:border-gray-800/50 rounded-lg focus:border-[#4cb6f9] focus:outline-none"
                    />
                    <button
                        onClick={handleSendCode}
                        className="w-full max-w-md py-3 rounded-lg text-[15px] font-bold bg-[#BDE6FF] text-black"
                    >
                        Send verification code
                    </button>
                </div>
            )}

            {step === "otp" && (
                <OtpInput
                    maskedEmail={otpMode === "setup" ? maskEmail(pendingEmail) : savedEmail}
                    onVerify={handleVerifyOtp}
                    onResend={handleResendOtp}
                    verifying={otpMode === "setup" ? verifyEmailLoading : pinResetVerifyLoading}
                />
            )}

            {(step === "createPin" || step === "resetPin") && (
                <div className="flex flex-col items-center px-4 pt-6">
                    <p className="text-[14px] font-semibold text-black dark:text-white mb-1">
                        Create a 4-digit PIN
                    </p>
                    <p className="text-[12px] text-gray-600 dark:text-gray-400 mb-4 text-center max-w-sm">
                        This PIN will be required to open and change Parental Controls.
                    </p>
                    <PinKeypad length={PIN_LENGTH} value={newPin} onChange={setNewPin} />
                </div>
            )}

            {(step === "confirmPin" || step === "resetConfirmPin") && (
                <div className="flex flex-col items-center px-4 pt-6">
                    <p className="text-[14px] font-semibold text-black dark:text-white mb-1">
                        Confirm your PIN
                    </p>
                    <p className="text-[12px] text-gray-600 dark:text-gray-400 mb-4 text-center max-w-sm">
                        Enter the same 4-digit PIN again.
                    </p>
                    <PinKeypad length={PIN_LENGTH} value={confirmPin} onChange={setConfirmPin} />
                </div>
            )}

            {step === "enterPin" && (
                <div className="flex flex-col items-center px-4 pt-6">
                    <p className="text-[14px] font-semibold text-black dark:text-white mb-1">
                        Enter your 4-digit PIN to access Parental Controls
                    </p>
                    {linkedEmail && (
                        <p className="text-[11px] text-gray-600 dark:text-gray-400 mb-3">
                            Linked to <span className="font-semibold">{maskEmail(linkedEmail)}</span>
                        </p>
                    )}
                    <PinKeypad length={PIN_LENGTH} value={enterPin} onChange={setEnterPin} error={enterPinError} />
                    <button
                        onClick={() => handleForgotPin("enterPin")}
                        className="text-[#4cb6f9] font-semibold text-[13px] mt-6 hover:underline"
                    >
                        Forgot PIN?
                    </button>
                </div>
            )}

            {step === "dashboard" && (
                <div className="px-3 pb-6 flex flex-col justify-between">
                    <div className="flex flex-col pt-3">
                        <p className="text-[11px] font-normal text-gray-600 dark:text-gray-400">
                            Linked parent email: <span className="font-semibold">{savedEmail}</span>
                        </p>

                        <div className="mt-4">
                            <p className="text-[14px] text-black dark:text-white font-bold">Neighborhood Access</p>
                            <div className="mt-2 border-2 border-[#BDE6FF] dark:border-gray-800/50 rounded-lg">
                                <div className="flex justify-between gap-x-14 px-3 py-2">
                                    <div>
                                        <p className="text-[14px] font-bold">Neighborhood</p>
                                        <p className="mt-1 text-[10px] font-normal">
                                            When off, Neighborhood is hidden and inaccessible on this account.
                                        </p>
                                    </div>
                                    <div
                                        className={`autoSaverSwitch relative inline-flex select-none items-center ${neighborhoodSaving ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                                        onClick={neighborhoodSaving ? undefined : handleToggleNeighborhood}
                                    >
                                        <span className={`slider flex h-[19px] w-[40px] items-center rounded-full p-1 duration-200 ${neighborhoodAllowed ? "bg-[#BDE6FF]" : "bg-[#d9d9d9]"}`}>
                                            <span className={`dot h-[13px] w-[13px] rounded-full bg-[#666666] shadow-2xl duration-200 ${neighborhoodAllowed ? "translate-x-5" : ""}`} />
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-4">
                            <div className="flex items-center justify-between">
                                <p className="text-[14px] text-black dark:text-white font-bold">Daily Time Limit</p>
                                {usageToday && (
                                    <p className="text-[10px] text-gray-600 dark:text-gray-400">
                                        {formatUsage(usageToday.today_usage_seconds)} used today
                                    </p>
                                )}
                            </div>
                            <div className="mt-2 border-2 border-[#BDE6FF] dark:border-gray-800/50 rounded-lg overflow-hidden">
                                <div
                                    onClick={() => setTimeLimitOpen((prev) => !prev)}
                                    className="flex items-center justify-between px-3 py-2 cursor-pointer"
                                >
                                    <p className="text-[13px] font-semibold">
                                        {TIME_LIMIT_OPTIONS.find((o) => o.value === dailyLimitMinutes)?.label || "No Limit"}
                                    </p>
                                    <motion.div
                                        animate={{ rotate: timeLimitOpen ? 180 : 0 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        <ChevronDown size={16} className="text-black dark:text-white" />
                                    </motion.div>
                                </div>
                                <AnimatePresence initial={false}>
                                    {timeLimitOpen && (
                                        <motion.div
                                            key="time-limit-options"
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.25, ease: "easeInOut" }}
                                            className="overflow-hidden border-t-2 border-[#BDE6FF] dark:border-gray-800/50 divide-y-2 divide-[#BDE6FF] dark:divide-gray-800/50"
                                        >
                                            {TIME_LIMIT_OPTIONS.map((option) => (
                                                <div
                                                    key={option.label}
                                                    onClick={() => {
                                                        setDailyLimitMinutes(option.value);
                                                        setTimeLimitOpen(false);
                                                    }}
                                                    className="flex items-center justify-between px-3 py-2 cursor-pointer"
                                                >
                                                    <p className="text-[13px] font-medium">{option.label}</p>
                                                    {dailyLimitMinutes === option.value && (
                                                        <Check size={16} className="text-[#4cb6f9]" />
                                                    )}
                                                </div>
                                            ))}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </div>

                        <div className="mt-4">
                            <p className="text-[14px] text-black dark:text-white font-bold">Security</p>
                            <div
                                onClick={() => handleForgotPin("dashboard")}
                                className="mt-2 flex items-center justify-between gap-x-3 border-2 border-[#BDE6FF] dark:border-gray-800/50 rounded-lg px-3 py-2 cursor-pointer"
                            >
                                <p className="text-[13px] font-bold">Change PIN</p>
                                <ChevronRight className="w-4 h-4 text-black dark:text-white" />
                            </div>
                        </div>

                        {auditLog?.length > 0 && (
                            <div className="mt-4">
                                <div className="border-2 border-[#BDE6FF] dark:border-gray-800/50 rounded-lg overflow-hidden">
                                    <div
                                        onClick={() => setRecentChangesOpen((prev) => !prev)}
                                        className="flex items-center justify-between px-3 py-2 cursor-pointer"
                                    >
                                        <p className="text-[14px] text-black dark:text-white font-bold">Recent Changes</p>
                                        <motion.div
                                            animate={{ rotate: recentChangesOpen ? 180 : 0 }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            <ChevronDown size={16} className="text-black dark:text-white" />
                                        </motion.div>
                                    </div>
                                    <AnimatePresence initial={false}>
                                        {recentChangesOpen && (
                                            <motion.div
                                                key="recent-changes-list"
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.25, ease: "easeInOut" }}
                                                className="overflow-hidden border-t-2 border-[#BDE6FF] dark:border-gray-800/50 divide-y-2 divide-[#BDE6FF] dark:divide-gray-800/50"
                                            >
                                                {auditLog.slice(0, 5).map((entry) => (
                                                    <div key={entry.id} className="px-3 py-2">
                                                        <p className="text-[12px] font-semibold text-black dark:text-white">
                                                            {entry.field_changed}: {entry.old_value} → {entry.new_value}
                                                        </p>
                                                        <p className="text-[10px] text-gray-600 dark:text-gray-400">
                                                            {formatAuditDate(entry.created_at)}
                                                        </p>
                                                    </div>
                                                ))}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </div>
                        )}
                    </div>

                    <button
                        className="mt-6 text-[13px] w-full bg-[#BDE6FF] text-black font-bold py-3 rounded-lg"
                        onClick={handleSaveDashboard}
                        disabled={saving || updateSettingsLoading}
                    >
                        {saving || updateSettingsLoading ? "Saving..." : "Save changes"}
                    </button>
                </div>
            )}
        </div>
    );
};

export default ParentalControls;
